import { restClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

// ─── Param Types ──────────────────────────────────────────────────────────────

export interface UserListParams {
  page?: number
  limit?: number
  search?: string
  user_types?: string
  city_id?: number
  vendor_id?: number
  is_active?: number
  is_verified?: number
  gear?: string
  vehicle_type_id?: number
  start_date?: string
  end_date?: string
  type?: string
  store_id?: number
  shipping_status_id?: number
  payment_method_system_name?: string
}

export interface AddressListParams {
  page?: number
  limit?: number
  search?: string
  user_id?: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const q = (p: Record<string, unknown>) =>
  new URLSearchParams(
    Object.entries(p)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .reduce((a, [k, v]) => ({ ...a, [k]: String(v) }), {})
  ).toString()

// ─── User Service ─────────────────────────────────────────────────────────────

export const userService = {
  // List users with full filter support (mirrors old useGetUsers)
  list: (p: UserListParams = {}) => {
    const params: Record<string, unknown> = {
      page: p.page ?? 1,  // UI sends 1-based page directly
      limit: p.limit ?? 15,
    }
    if (p.search) params.search = p.search
    if (p.user_types && p.user_types !== 'all') params['user_types[]'] = p.user_types
    if (p.is_active !== undefined && p.is_active !== null) params.is_active = p.is_active
    if (p.city_id) params.city_id = p.city_id
    if (p.vehicle_type_id) params.vehicle_type_id = p.vehicle_type_id
    if (p.gear !== undefined && p.gear !== null) params.gear = p.gear
    if (p.vendor_id) params.vendor_id = p.vendor_id
    if (p.start_date) params.start_date = p.start_date
    if (p.end_date) params.end_date = p.end_date
    if (p.type) params.type = p.type
    if (p.store_id) params.store_id = p.store_id
    if (p.is_verified !== undefined && p.is_verified !== null) params.is_verified = p.is_verified
    return restClient.get(`${endpoints.users.list}?${q(params)}`).then(r => r.data)
  },

  // Get user by ID (mirrors old useGetUserById / useGetUserDetails)
  getById: (id: number) =>
    restClient.get(`${endpoints.users.getbyId}${id}`).then(r => r.data),

  // Get trainer details by ID (mirrors old useGetUserById with details endpoint)
  getTrainerById: (id: number) =>
    restClient.get(`${endpoints.users.details}${id}`).then(r => r.data),

  // Get user via assistant endpoint (mirrors old useGetAssistantUserDetails)
  getAssistantUserById: (id: number) =>
    restClient.get(`${endpoints.assistant.users.getUserById}${id}`).then(r => r.data),

  // Create regular user (mirrors old createUser)
  create: (data: Record<string, unknown>) =>
    restClient.post(endpoints.users.create, data).then(r => r.data),

  // Update user (mirrors old updateUser)
  update: (data: Record<string, unknown>) =>
    restClient.post(endpoints.users.update, data).then(r => r.data),

  // Delete user (mirrors old deleteUser — note: old used user_id param)
  delete: (id: number) =>
    restClient.delete(`${endpoints.users.delete}?user_id=${id}`).then(r => r.data),

  // Create trainer (mirrors old createTrainer)
  createTrainer: (data: Record<string, unknown>) =>
    restClient.post(endpoints.trainer.createTrainer, data).then(r => r.data),

  // Public trainers list, scoped to a school via vendor_id (used for e.g. Change Trainer)
  listTrainers: (p: { vendor_id?: number; page?: number; limit?: number; search?: string } = {}) => {
    const params: Record<string, unknown> = { limit: p.limit ?? 15 }
    if (p.vendor_id) params.vendor_id = p.vendor_id
    if (p.page) params.page = p.page
    if (p.search) params.search = p.search
    return restClient.get(`${endpoints.users.listTrainers}?${q(params)}`).then(r => r.data)
  },

  // ─── Enum Lookups ────────────────────────────────────────────────────────────

  getUserTypeEnum: () =>
    restClient.get(endpoints.users.enum).then(r => r.data),

  getGenderEnum: () =>
    restClient.get(endpoints.users.genderenum).then(r => r.data),

  getGearEnum: () =>
    restClient.get(endpoints.users.gearenum).then(r => r.data),

  // ─── Address ────────────────────────────────────────────────────────────────

  // List addresses for a user (mirrors old useGetAddressList)
  listAddresses: (p: AddressListParams = {}) => {
    const params: Record<string, unknown> = {}
    if (typeof p.page === 'number') params.page = p.page + 1
    if (p.limit && p.limit > 0) params.limit = p.limit
    if (p.search) params.search = p.search
    if (p.user_id) params.user_id = p.user_id
    return restClient.get(`${endpoints.users.addressList}?${q(params)}`).then(r => r.data)
  },

  // Create or update a user address (mirrors old createNewAddressForUser + updateExistingUserAddress)
  saveAddress: (data: Record<string, unknown>) =>
    restClient.post(endpoints.users.createAddress, data).then(r => r.data),

  // Delete a user address by address ID (mirrors old deleteUserAddress)
  deleteAddress: (addressId: number) =>
    restClient.delete(`${endpoints.users.deleteAddress}/${addressId}`).then(r => r.data),

  // ─── User Documents ──────────────────────────────────────────────────────────

  createOrUpdateDocument: (data: Record<string, unknown> | FormData) =>
    restClient.post(endpoints.users.userDocument.createOrUpdate, data).then(r => r.data),

  getDocumentList: (params: Record<string, unknown>) =>
    restClient.get(`${endpoints.users.userDocument.getList}?${q(params)}`).then(r => r.data),

  getDocumentById: (id: number) =>
    restClient.get(`${endpoints.users.userDocument.getById}/${id}`).then(r => r.data),

  deleteDocumentById: (id: number) =>
    restClient.delete(`${endpoints.users.userDocument.deleteById}/${id}`).then(r => r.data),

  deleteAllDocumentsByUserId: (userId: number) =>
    restClient.delete(`${endpoints.users.userDocument.deleteAllByUserId}/${userId}`).then(r => r.data),

  approveDocument: (data: Record<string, unknown>) =>
    restClient.post(endpoints.users.userDocument.approve, data).then(r => r.data),

  // ─── Photo upload ────────────────────────────────────────────────────────────
  // The create/update user endpoints take `photo_url` as a plain string, so the
  // file is uploaded here first and the returned URL is sent instead.
  uploadPhoto: (file: File): Promise<string> => {
    const fd = new FormData()
    fd.append('picture', file)
    return restClient.post(endpoints.allimage.createSingle, fd).then(r => {
      const d: any = r.data
      const url = d?.data?.url ?? d?.data?.picture_url ?? d?.data?.path ?? d?.url ?? d?.picture_url
      if (!url) throw new Error('Photo upload did not return a URL')
      return String(url)
    })
  },
}
